import React from 'react';
import { createPortal } from 'react-dom';
import './PhotoLightbox.css';

interface PhotoLightboxProps {
  /** Full-size image URLs. */
  images: string[];
  /** Index to open at; `null` closes the viewer. */
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  label?: string;
}

/**
 * Full-screen photo viewer.
 *
 * Rendered through a PORTAL to document.body, and that is the whole point rather
 * than a detail: `position: fixed` is only viewport-relative while no ancestor
 * establishes a containing block, and any transform/filter/perspective does
 * establish one. The feed card lifts itself on hover with
 * `transform: translateY(-1px)`, so an in-tree overlay was being clipped to the
 * card the user had just clicked — a "full-screen" viewer the size of a post.
 * A portal removes the whole class of bug: no ancestor is left to contain us.
 *
 * The image is sized to the viewport (not the source), capped so nothing is
 * cropped and nothing overflows, over a blurred backdrop of the page behind.
 */
const PhotoLightbox: React.FC<PhotoLightboxProps> = ({
  images,
  index,
  onClose,
  onIndexChange,
  label = 'Photo',
}) => {
  const isOpen = index !== null && Boolean(images[index]);
  const hasMultiple = images.length > 1;

  const showPrevious = React.useCallback(() => {
    if (index === null || !hasMultiple) return;
    onIndexChange((index - 1 + images.length) % images.length);
  }, [hasMultiple, images.length, index, onIndexChange]);

  const showNext = React.useCallback(() => {
    if (index === null || !hasMultiple) return;
    onIndexChange((index + 1) % images.length);
  }, [hasMultiple, images.length, index, onIndexChange]);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') showPrevious();
      if (event.key === 'ArrowRight') showNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, showNext, showPrevious]);

  // The page must not scroll under the viewer. Restoring the previous inline
  // value (rather than clearing it) keeps this safe if anything else is also
  // holding the scroll lock.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen || index === null) return null;

  return createPortal(
    <div
      className="v2-photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={hasMultiple ? `${label} ${index + 1} of ${images.length}` : label}
    >
      <button
        type="button"
        className="v2-photo-lightbox__backdrop"
        onClick={onClose}
        aria-label="Close photo viewer"
      />

      <button
        type="button"
        className="v2-photo-lightbox__close"
        onClick={onClose}
        aria-label="Close photo viewer"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {hasMultiple && (
        <>
          <button
            type="button"
            className="v2-photo-lightbox__nav v2-photo-lightbox__nav--prev"
            onClick={showPrevious}
            aria-label="Previous photo"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M15 5l-7 7 7 7"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="v2-photo-lightbox__nav v2-photo-lightbox__nav--next"
            onClick={showNext}
            aria-label="Next photo"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M9 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="v2-photo-lightbox__counter">
            {index + 1} / {images.length}
          </div>
        </>
      )}

      {/* Keyed by src so switching photos restarts decoding instead of showing
          the previous frame stretched to the new one's box. */}
      <img
        key={images[index]}
        src={images[index]}
        alt=""
        className="v2-photo-lightbox__image"
      />
    </div>,
    document.body,
  );
};

export default PhotoLightbox;
